import { ShiftSettlementBlockerReason } from '@shared/api/graphql/gql/graphql';

import type { FiscalShiftAction, FiscalShiftState } from './fiscal-shift-state';

import { fiscalShiftStatusDomain } from './fiscal-shift-state';

/** Что кассир пытается сделать. Влияет только на формулировку, не на набор блокеров. */
export type OrderShiftBlockerIntent = 'payment' | 'refund';

export interface OrderShiftBlocker {
  action: Exclude<FiscalShiftAction, null> | null;
  /** Вторая строка: проблема ККТ поверх блокера смены. null — если касса в порядке. */
  hint: string | null;
  title: string;
}

export interface OrderShiftBlockerInput {
  fiscalShift: FiscalShiftState | null;
  intent: OrderShiftBlockerIntent;
  shiftBlockerReason: ShiftSettlementBlockerReason | null | undefined;
  shiftRequired: boolean | null | undefined;
}

/**
 * Единственное правило «почему нельзя рассчитать» для экранов оплаты и возврата.
 *
 * Серверный `shiftBlockerReason` приоритетнее клиентского `fiscalShift`: backend всё равно
 * откажет в расчёте. Клиентский источник остаётся fallback'ом для состояний, которых сервер
 * не видит, — например, когда смену на ККТ закрыли извне, а смена Морса жива.
 *
 * Заголовок берётся только из статусов домена `'shift'`; проблема ККТ никогда не подменяет
 * заголовок, а становится второй строкой (`hint`).
 */
export function orderShiftBlocker(input: OrderShiftBlockerInput): OrderShiftBlocker | null {
  const hint = kktHint(input.fiscalShift);
  const serverBlocker =
    input.shiftRequired === true ? serverShiftBlocker(input.shiftBlockerReason, input.intent) : null;

  if (serverBlocker !== null) {
    return { ...serverBlocker, hint };
  }

  const { fiscalShift } = input;

  if (fiscalShift !== null && fiscalShift.blocking && fiscalShiftStatusDomain[fiscalShift.status] === 'shift') {
    return { action: fiscalShift.action, hint, title: fiscalShift.title };
  }

  return null;
}

function serverShiftBlocker(
  reason: ShiftSettlementBlockerReason | null | undefined,
  intent: OrderShiftBlockerIntent,
): Omit<OrderShiftBlocker, 'hint'> | null {
  switch (reason) {
    case ShiftSettlementBlockerReason.Closing:
      return { action: 'continue_closing', title: 'Закрытие смены не завершено' };

    case ShiftSettlementBlockerReason.Expired:
      return { action: 'close', title: 'Смена идёт больше суток. Закройте её и откройте новую' };

    case ShiftSettlementBlockerReason.ForeignKkt:
      return {
        action: null,
        title: `Смена открыта на другой кассе. Принимать ${
          intent === 'refund' ? 'возврат' : 'оплату'
        } можно на том компьютере, где открыта смена`,
      };

    case ShiftSettlementBlockerReason.NotOpen:
      return { action: 'open', title: 'Смена не открыта' };

    case ShiftSettlementBlockerReason.Opening:
      return { action: 'continue_opening', title: 'Открытие смены не завершено' };

    default:
      // Сервер требует смену, но причину не назвал — решает клиентское состояние.
      return null;
  }
}

/** Проблема железа поверх блокера смены: заголовок не подменяет, идёт второй строкой. */
function kktHint(fiscalShift: FiscalShiftState | null): string | null {
  return fiscalShift !== null && fiscalShift.blocking && fiscalShiftStatusDomain[fiscalShift.status] === 'kkt'
    ? fiscalShift.title
    : null;
}
